import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { AdminUserRealm } from 'further-types/admin-user';
import { Vehicle } from 'further-types/lp/vehicle';
import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import React, { useState, useContext, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';

export type Breadcrumb =
  | string
  | {
      label: string;
      link: string;
    };

type LayoutContextType = {
  isSidebarOpen: boolean;
  setSidebarOpen: (isSidebarOpen: boolean) => void;
  selectedVehicleId: string | null;
  switchVehicle: (vehicleId: string) => void;
  isVehicleSwitching: boolean;
  vehicle?: Vehicle;
  breadcrumbs: Array<Breadcrumb>;
  setBreadcrumbs: (breadcrumbs: Array<Breadcrumb>) => void;
  availableVehicles: Array<Vehicle>;
  pageIcon: OverridableComponent<SvgIconTypeMap<object>> | null;
  setPageIcon: (
    icon: OverridableComponent<SvgIconTypeMap<object>> | null,
  ) => void;
};

const LayoutContext = React.createContext<LayoutContextType>({
  isSidebarOpen: false,
  setSidebarOpen: () => {},
  selectedVehicleId: null,
  switchVehicle: () => {},
  isVehicleSwitching: false,
  vehicle: undefined,
  breadcrumbs: [],
  setBreadcrumbs: () => {},
  availableVehicles: [],
  pageIcon: null,
  setPageIcon: () => {},
});

const LayoutContextProvider: React.FC<{
  children: React.ReactNode;
  availableVehicles: Array<Vehicle>;
}> = ({ children, availableVehicles }) => {
  const { profile } = useCurrentUser();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<Breadcrumb>>([]);
  const [pageIcon, setPageIcon] = useState<OverridableComponent<
    SvgIconTypeMap<object>
  > | null>(null);
  const vehicleStorageKey = `selectedVehicleId-${
    profile?.data?.auth0OrgId || 'default'
  }`;
  const [selectedVehicleId, setSelectedVehicleId] = useLocalStorage<
    string | null
  >(vehicleStorageKey, null);
  const [isVehicleSwitching, setIsVehicleSwitching] = useState(false);

  const switchVehicle = async (vehicleId: string) => {
    if (!availableVehicles.some((vehicle) => vehicle._id === vehicleId)) {
      return;
    }

    setIsVehicleSwitching(true);
    // await a 500s timeout
    await new Promise((resolve) => setTimeout(resolve, 500));
    setSelectedVehicleId(vehicleId);
    setIsVehicleSwitching(false);
  };

  const vehicle = availableVehicles.find(
    (vehicle) => vehicle._id === selectedVehicleId,
  );

  useEffect(() => {
    if (!vehicle && availableVehicles.length > 0) {
      switchVehicle(availableVehicles[0]._id);
    }
  }, [vehicle?._id, availableVehicles]);

  const currentSelectedVehicleId =
    profile?.data?.realm === AdminUserRealm.Lp ? vehicle?._id ?? null : null;

  const layoutContextValue = {
    isSidebarOpen,
    setSidebarOpen,
    selectedVehicleId: currentSelectedVehicleId,
    switchVehicle,
    isVehicleSwitching,
    vehicle,
    breadcrumbs,
    setBreadcrumbs,
    availableVehicles,
    pageIcon,
    setPageIcon,
  };

  return (
    <LayoutContext.Provider value={layoutContextValue}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayoutContext = () => useContext(LayoutContext);

export default LayoutContextProvider;
